import React, {useEffect, useMemo, useState, useRef} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import {Button, designSystemToken, Icon} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import {
  CONTENT_STUDIO_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import contentStudioStore, {
  contentStudioActions,
} from '@/stores/contentStudioStore';
import {useDefaultProjectName} from '../projects/utils';
import SrtDisplayRow from './SrtDisplayRow';

function RephraseEditor({
  currentIndex,
  setCurrentIndex,
  applyRephrase,
  isSubmitting = false,
}: {
  currentIndex: string | null;
  setCurrentIndex: (index: string | null) => void;
  applyRephrase: () => void;
  isSubmitting?: boolean;
}) {
  const {uiInteraction} = useUiInteractionEvent();
  const currentRephraseProject = contentStudioStore(
    useShallow((state) => state.currentRephraseProject)
  );
  const defaultProjectName = useDefaultProjectName(translate('Rescript'));
  const editorRef = useRef<HTMLDivElement>(null);

  const isRephraseProjectEdited = useMemo(() => {
    return currentRephraseProject?.currentSrt.some(
      (srtItem) => srtItem.currentText !== srtItem.originalText
    );
  }, [currentRephraseProject]);
  const [projectName, setProjectName] = useState(
    currentRephraseProject?.projectName || defaultProjectName
  );

  useEffect(() => {
    if (currentRephraseProject?.projectName) {
      setProjectName(currentRephraseProject.projectName);
    } else {
      setProjectName(defaultProjectName);
    }
  }, [currentRephraseProject?.projectName, defaultProjectName]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        editorRef.current &&
        !editorRef.current.contains(event.target as Node)
      ) {
        setCurrentIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setCurrentIndex]);

  useEffect(() => {
    const currentIndexId = currentRephraseProject?.currentSrt?.[0]?.id;

    if (currentIndexId && !currentIndex) {
      setCurrentIndex(currentIndexId);
    }
  }, [currentRephraseProject, setCurrentIndex]);

  const disabled =
    !currentRephraseProject || !isRephraseProjectEdited || isSubmitting;
  return (
    <>
      <Stack
        ref={editorRef}
        direction="column"
        spacing="36px"
        bgcolor={designSystemToken('semantic.bg.secondary')}
        borderRadius="8px"
        flex={1}
        overflow="auto"
        paddingX="20px"
        paddingY="30px"
      >
        {!currentRephraseProject ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress />
          </Box>
        ) : (
          currentRephraseProject?.currentSrt.map((item) => {
            return (
              <SrtDisplayRow
                key={item.id}
                item={item}
                isEditing={item.id === currentIndex}
                isEdited={item.currentText !== item.originalText}
                currentIndex={currentIndex}
                setCurrentIndex={setCurrentIndex}
                editRow={(newText) => {
                  contentStudioActions.updateCurrentRephraseProjectSrt(
                    currentRephraseProject.currentSrt.map((row) =>
                      row.id === currentIndex
                        ? {...row, currentText: newText}
                        : row
                    )
                  );
                }}
              />
            );
          })
        )}
      </Stack>
      <Stack direction="row" alignItems="center">
        <TextField
          variant="outlined"
          label={translate('Insert project name')}
          placeholder={translate('Enter a project name')}
          value={projectName}
          onChange={(e) => {
            contentStudioActions.updateCurrentRephraseProjectName(
              e.target.value
            );
            setProjectName(e.target.value);
          }}
          sx={{marginRight: 'auto'}}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon
                  name="Navigation-Edit"
                  size="medium"
                  appearance="primary"
                />
              </InputAdornment>
            ),
          }}
        />
        <Button
          appearance="neutral"
          mode="filled"
          size="large"
          sx={{marginLeft: '16px', width: '200px'}}
          icon={
            <Icon
              name="Features-Rename"
              size="large"
              appearance={disabled ? 'disabled' : 'white'}
            />
          }
          iconPosition="end"
          disabled={disabled}
          isLoading={isSubmitting}
          onClick={() => {
            uiInteraction({
              item_name: 'rephrase_editor_apply_edits',
              interaction_location: CONTENT_STUDIO_VIEW_NAMES.REPHRASE_EDITOR,
              flow_name: FLOW_NAMES.CONTENT_STUDIO.REPHRASE,
            });
            applyRephrase();
          }}
        >
          {translate('Rescript')}
        </Button>
      </Stack>
    </>
  );
}

export default RephraseEditor;
